import React, { useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { InlineTextButton } from '../index';
import css from './TranslateButton.module.css';

const stripHtml = html => html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ');

// Detect whether text is more likely French or English
const detectLanguage = text => {
  const sample = text.slice(0, 500).toLowerCase();
  const fr = (sample.match(/\b(le|la|les|de|du|des|un|une|est|sont|je|tu|il|elle|nous|vous|ils|elles|et|en|que|qui|dans|sur|pour|pas|avec|au|aux|par|ce|se|ne|on|plus|tout|mais|ou|donc|or|ni|car)\b/g) || []).length;
  const en = (sample.match(/\b(the|is|are|was|were|it|in|of|and|to|that|this|with|for|on|at|by|be|have|has|had|will|would|could|should|from|they|their|about|which|when|we|my|your|our|not|but|an|a)\b/g) || []).length;
  return fr > en ? 'fr' : 'en';
};

/**
 * TranslateButton
 *
 * Only renders if the detected content language differs from the user's locale.
 *
 * Props:
 *  - text: string — plain text content to translate
 *  - html: string — HTML content to translate (takes priority over text)
 *  - onTranslate: fn({ text, html, targetLang }) — duck action to call
 *  - onTranslated: fn(translatedText) — called with translated content
 *  - onShowOriginal: fn() — called when user toggles back to original
 */
const TranslateButton = ({ text, html, onTranslate, onTranslated, onShowOriginal }) => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [translated, setTranslated] = useState(false);
  const [error, setError] = useState(null);

  const targetLang = intl.locale === 'fr' ? 'fr' : 'en';
  const targetLangLabel = targetLang === 'fr' ? 'Français' : 'English';

  const contentLang = useMemo(() => {
    const raw = html ? stripHtml(html) : (text || '');
    return detectLanguage(raw);
  }, [text, html]);

  // Don't show button if content is already in the user's language (and not yet translated)
  if (!translated && contentLang === targetLang) {
    return null;
  }

  const handleTranslate = async e => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    setError(null);
    try {
      const result = await onTranslate({ text, html, targetLang });
      setTranslated(true);
      onTranslated(result.translatedText);
    } catch (e) {
      setError(e.message || 'Translation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleShowOriginal = e => {
    e.preventDefault();
    e.stopPropagation();
    setTranslated(false);
    onShowOriginal();
  };

  if (error) {
    return <span className={css.error}>{error}</span>;
  }

  if (loading) {
    return <span className={css.loading}>Translating...</span>;
  }

  return (
    <InlineTextButton
      className={css.translateBtn}
      type="button"
      onClick={translated ? handleShowOriginal : handleTranslate}
    >
      {translated ? 'Show original' : `Translate to ${targetLangLabel}`}
    </InlineTextButton>
  );
};

export default TranslateButton;
